import type { EditorState, Transaction } from "prosemirror-state";
import { TextSelection } from "prosemirror-state";
import { schema } from "./schema";

// Max length of the visible text generated from a bare URL (auto-detect, or a
// link inserted without explicit text). Longer URLs are middle-or-tail elided.
const MAX_URL_TEXT = 42;

/** A display label for a URL: the URL itself, truncated with an ellipsis. */
export function truncateUrl(url: string, max: number = MAX_URL_TEXT): string {
  if (url.length <= max) return url;
  return url.slice(0, max - 1).trimEnd() + "…";
}

/** If `pos` sits inside an existing link, return its full range and href;
 * otherwise null. Used so the toolbar button edits a link in place. */
export function findLinkAround(
  state: EditorState,
  pos: number
): { from: number; to: number; href: string } | null {
  const link = schema.marks["link"]!;
  const $pos = state.doc.resolve(pos);
  const parent = $pos.parent;
  const start = parent.childAfter($pos.parentOffset);
  if (!start.node) return null;
  const mark = link.isInSet(start.node.marks);
  if (!mark) return null;

  let startIndex = $pos.index();
  let endIndex = startIndex + 1;
  let from = $pos.start() + start.offset;
  let to = from + start.node.nodeSize;
  while (startIndex > 0 && mark.isInSet(parent.child(startIndex - 1).marks)) {
    startIndex--;
    from -= parent.child(startIndex).nodeSize;
  }
  while (endIndex < parent.childCount && mark.isInSet(parent.child(endIndex).marks)) {
    to += parent.child(endIndex).nodeSize;
    endIndex++;
  }
  return { from, to, href: mark.attrs["href"] as string };
}

/** Build a transaction inserting an external link over `[from, to]`. `text`
 * empty → a truncated form of the URL. Cursor lands just after the link. */
export function insertLinkTr(
  state: EditorState,
  from: number,
  to: number,
  url: string,
  text: string
): Transaction {
  const label = text.trim() || truncateUrl(url);
  const mark = schema.marks["link"]!.create({ href: url });
  const node = schema.text(label, [mark]);
  const tr = state.tr.replaceRangeWith(from, to, node);
  const end = from + node.nodeSize;
  tr.setSelection(TextSelection.create(tr.doc, end));
  return tr.scrollIntoView();
}
